/**
 * Download Center: everything the queue is bringing down, grouped by creative.
 * The twin of the Upload Center. Files come one at a time, in the order picked,
 * each named with the creative, the kind and the folder it came from.
 */
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Download, Check, AlertTriangle, RotateCcw, Trash2, Loader2, FolderOpen, Pause, Play, Ban } from 'lucide-react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { PageHeader } from '@/components/ui/Card';
import { useDownloadCenter, type DownloadJob } from '@/lib/download-center';
import { formatBytes, formatSpeed, formatEta } from '@/lib/upload-center';
import { cn } from '@/lib/cn';

const STATE_LABEL: Record<DownloadJob['state'], string> = {
  queued: 'waiting', downloading: 'downloading', paused: 'paused', handed: 'in your browser', done: 'saved', failed: 'failed', cancelled: 'cancelled',
};

function stageText(j: DownloadJob): string {
  if (j.state === 'failed') return j.error ?? 'failed';
  if (j.state === 'done') return `saved as ${j.name}`;
  if (j.state === 'handed') return "handed to the browser's own downloads";
  if (j.state === 'queued' && j.attempts > 0) return `${j.error ?? 'broke off'}${j.loaded ? `, ${formatBytes(j.loaded)} kept` : ''}`;
  if (j.state === 'queued') return 'waiting its turn';
  if (j.state === 'cancelled') return 'cancelled';
  if (j.state === 'paused') return `paused${j.loaded ? `, ${formatBytes(j.loaded)} of ${formatBytes(j.size)} kept` : ''}`;
  if (j.mode === 'browser') return 'starting in the browser';
  const got = `${formatBytes(j.loaded)} of ${formatBytes(j.size)}`;
  const rate = formatSpeed(j.bps);
  const left = j.bps && j.bps > 0 ? formatEta(Math.round(Math.max(0, j.size - j.loaded) / j.bps)) : '';
  return got + (rate ? ` at ${rate}` : '') + (left ? `, ${left} left` : '');
}

export default function DownloadCenterPage() {
  const dl = useDownloadCenter();
  const { jobs, active, queued, paused, failed, done, pendingBytes, bytesPerSec, etaSeconds, folderName } = dl;
  const [confirmCancel, setConfirmCancel] = useState(false);

  const groups = new Map<string, DownloadJob[]>();
  for (const j of jobs) {
    const list = groups.get(j.creativeId);
    if (list) list.push(j); else groups.set(j.creativeId, [j]);
  }
  const ordered = Array.from(groups.entries()).sort((a, b) =>
    Math.max(...b[1].map((j) => j.queuedAt)) - Math.max(...a[1].map((j) => j.queuedAt)));

  const browserMode = jobs.length > 0 && jobs.every((j) => j.mode === 'browser');
  const speed = formatSpeed(bytesPerSec);
  const eta = formatEta(etaSeconds);
  const subtitle = jobs.length === 0
    ? 'Nothing downloading.'
    : `${active} downloading, ${queued} waiting${paused ? `, ${paused} paused` : ''}, ${done} done${failed ? `, ${failed} failed` : ''}`
      + (browserMode ? '' : (pendingBytes > 0 ? ` - ${formatBytes(pendingBytes)} left` : ''))
      + (speed ? ` at ${speed}` : '')
      + (eta ? `, about ${eta} to go` : '');

  return (
    <div>
      <PageHeader
        title="Download Center"
        subtitle={subtitle}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            {(dl.active + dl.queued) > 0 && (
              <button onClick={dl.pauseAll} data-testid="pause-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <Pause size={12} /> Pause all
              </button>
            )}
            {dl.paused > 0 && (
              <button onClick={dl.resumeAll} data-testid="resume-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <Play size={12} /> Resume {dl.paused} paused
              </button>
            )}
            {(dl.active + dl.queued + dl.paused) > 0 && (
              <button onClick={() => setConfirmCancel(true)} data-testid="cancel-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800 hover:text-red-300">
                <Ban size={12} /> Cancel all
              </button>
            )}
            {failed > 0 && (
              <button onClick={dl.retryAllFailed}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <RotateCcw size={12} /> Retry {failed} failed
              </button>
            )}
            {jobs.some((j) => j.state !== 'queued' && j.state !== 'downloading') && (
              <button onClick={dl.clearFinished}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <Trash2 size={12} /> Clear finished
              </button>
            )}
          </div>
        )}
      />

      {jobs.length > 0 && (
        <div className="mb-3 text-[11px] text-muted inline-flex items-center gap-1.5" data-testid="download-mode">
          <FolderOpen size={12} />
          {folderName && !browserMode
            ? <>Saving into <span className="text-muted-strong">{folderName}</span>, one file at a time, in order.</>
            : <>Each file is handed to the browser in turn; its own download list shows the bytes.</>}
        </div>
      )}

      {confirmCancel && (
        <ConfirmDialog
          open
          tone="danger"
          title="Cancel everything still to go?"
          message="Every file waiting, moving or paused stops, and what was already sent of them is thrown away. Finished files are not touched."
          confirmLabel="Cancel all"
          cancelLabel="Keep going"
          onConfirm={() => { setConfirmCancel(false); dl.cancelAll(); }}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
      {jobs.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-bg-card/40 py-16 text-center text-muted text-sm">
          <Download size={28} className="mx-auto mb-2 text-muted/50" />
          Nothing in the queue.
        </div>
      )}

      <div className="space-y-4" data-testid="download-center-groups">
        {ordered.map(([creativeId, list]) => {
          const g = list[0];
          const gLive = list.filter((j) => j.state === 'downloading' || j.state === 'queued' || j.state === 'paused').length;
          const gDone = list.filter((j) => j.state === 'done' || j.state === 'handed').length;
          const gFailed = list.filter((j) => j.state === 'failed').length;
          return (
            <div key={creativeId} className="rounded-xl border border-slate-800 bg-bg-card/40 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-800 flex flex-wrap items-center gap-2">
                <Link to={`/creatives/${creativeId}`} className="text-sm text-muted-strong hover:text-accent-400 font-medium">
                  {g.creativeCode || 'Creative'}
                </Link>
                <span className="text-xs text-muted truncate max-w-[40ch]">{g.creativeTitle}</span>
                <span className="ml-auto text-[11px] text-muted">
                  {gDone}/{list.length} done{gLive ? ` - ${gLive} to go` : ''}{gFailed ? ` - ${gFailed} failed` : ''}
                </span>
              </div>
              <div className="divide-y divide-slate-800/70">
                {list.map((j) => <JobRow key={j.id} j={j} on={{
                  retry: () => dl.retry(j.id), cancel: () => dl.cancel(j.id), pause: () => dl.pause(j.id),
                  resume: () => dl.resume(j.id), remove: () => dl.remove(j.id) }} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface RowActions { retry: () => void; cancel: () => void; pause: () => void; resume: () => void; remove: () => void }

function JobRow({ j, on }: { j: DownloadJob; on: RowActions }) {
  return (
    <div className="px-4 py-2.5" data-testid="download-job" data-state={j.state}>
      <div className="flex items-center gap-2">
        {j.state === 'downloading' && <Loader2 size={13} className="text-accent-400 animate-spin shrink-0" />}
        {(j.state === 'done' || j.state === 'handed') && <Check size={13} className="text-emerald-400 shrink-0" />}
        {j.state === 'failed' && <AlertTriangle size={13} className="text-red-400 shrink-0" />}
        {(j.state === 'queued' || j.state === 'cancelled') && <Download size={13} className="text-muted shrink-0" />}
        {j.state === 'paused' && <Pause size={13} className="text-amber-300 shrink-0" />}

        <span className="text-xs text-muted-strong truncate" title={j.name}>{j.name}</span>
        {j.category && <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-800 text-muted shrink-0">{j.category}</span>}

        <span className="ml-auto text-[11px] text-muted shrink-0">{STATE_LABEL[j.state]}</span>
        {j.state === 'downloading' && j.mode === 'folder' && <span className="text-[11px] text-accent-400 w-9 text-right shrink-0">{j.pct}%</span>}

        {j.state === 'paused' && (
          <button onClick={on.resume} title="Carry on from where it stopped" aria-label="Resume" data-testid="download-resume"
            className="text-muted hover:text-accent-400 shrink-0"><Play size={13} /></button>
        )}
        {(j.state === 'queued' || (j.state === 'downloading' && j.mode === 'folder')) && (
          <button onClick={on.pause} title="Pause, keeping what is already sent" aria-label="Pause" data-testid="download-pause"
            className="text-muted hover:text-accent-400 shrink-0"><Pause size={13} /></button>
        )}
        {(j.state === 'failed' || j.state === 'cancelled') && (
          <button onClick={on.retry} title="Try this file again" aria-label="Retry"
            className="text-muted hover:text-accent-400 shrink-0"><RotateCcw size={12} /></button>
        )}
        {(j.state === 'queued' || (j.state === 'downloading' && j.mode === 'folder') || j.state === 'paused') && (
          <button onClick={on.cancel} title="Cancel, throwing away what was sent" aria-label="Cancel" data-testid="download-cancel"
            className="text-muted hover:text-red-300 shrink-0"><Ban size={13} /></button>
        )}
        <button onClick={on.remove} title="Delete from this list" aria-label="Delete from the list" data-testid="download-delete"
          className="text-muted hover:text-red-300 shrink-0"><Trash2 size={12} /></button>
      </div>

      {j.state === 'downloading' && j.mode === 'folder' && (
        <div className="h-1 rounded bg-slate-800 mt-1.5 overflow-hidden">
          <div className="h-full bg-accent-500 transition-all" style={{ width: `${j.pct}%` }} />
        </div>
      )}
      <div className={cn('text-[11px] mt-1', j.state === 'failed' ? 'text-red-300' : 'text-muted')}>
        {stageText(j)}
      </div>
    </div>
  );
}
